import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Mail, MessageSquare, KeyRound, LogOut, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errorText, fmtDate } from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { PageHeader, Card, Button, Label, inputCls, Badge } from "@/admin/ui";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const [profile, setProfile] = useState({ name: user.name, email: user.email || "", phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [totpPw, setTotpPw] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(errorText(err)); } finally { setBusy(""); }
  };

  const saveProfile = () => run("profile", async () => {
    await api.patch("/profile", profile);
    await refresh();
    toast.success("Profile updated");
  });

  const changePw = () => run("pw", async () => {
    if (pw.new_password !== pw.confirm) throw { response: { data: { detail: "New passwords do not match" } } };
    await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
    toast.success("Password changed — please sign in again");
    await logout();
  });

  const resetTotp = () => run("totp", async () => {
    const { data } = await api.post("/auth/totp/reset", { password: totpPw });
    setSetup(data);
    setTotpPw("");
    await refresh();
  });

  const confirmTotp = () => run("confirm", async () => {
    await api.post("/auth/totp/confirm", { code });
    setSetup(null);
    setCode("");
    await refresh();
    toast.success("Authenticator app connected");
  });

  const methods = user.methods || {};

  return (
    <div data-testid="admin-account">
      <PageHeader eyebrow="Account & security" title="Your admin account" sub="Only you can sign in here. Keep the password long, and keep your authenticator app safe." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="account-profile">
          <h2 className="text-base md:text-lg font-semibold text-[#0B1528] mb-5">Profile</h2>
          <div className="space-y-4">
            <div><Label htmlFor="acc-name">Full name</Label><input id="acc-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="account-name-input" /></div>
            <div><Label htmlFor="acc-username">Username</Label><input id="acc-username" value={user.username} disabled className={`${inputCls} bg-slate-50 text-slate-500`} /></div>
            <div><Label htmlFor="acc-email">Email (for email codes)</Label><input id="acc-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={inputCls} data-testid="account-email-input" /></div>
            <div><Label htmlFor="acc-phone">Phone (for SMS codes, with country code)</Label><input id="acc-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+91…" className={inputCls} data-testid="account-phone-input" /></div>
            <Button onClick={saveProfile} loading={busy === "profile"} data-testid="account-save-profile">Save profile</Button>
          </div>
        </Card>

        <Card data-testid="account-password">
          <h2 className="text-base md:text-lg font-semibold text-[#0B1528] mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-500" /> Change password</h2>
          <p className="text-xs text-slate-500 mb-5">Minimum 10 characters. You'll be signed out everywhere afterwards.</p>
          <div className="space-y-4">
            <div><Label htmlFor="pw-cur">Current password</Label><input id="pw-cur" type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="account-current-password" /></div>
            <div><Label htmlFor="pw-new">New password</Label><input id="pw-new" type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="account-new-password" /></div>
            <div><Label htmlFor="pw-conf">Confirm new password</Label><input id="pw-conf" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="account-confirm-password" /></div>
            <Button onClick={changePw} loading={busy === "pw"} disabled={!pw.current_password || pw.new_password.length < 10} data-testid="account-change-password">Update password</Button>
          </div>
        </Card>

        <Card className="lg:col-span-2" data-testid="account-2fa">
          <h2 className="text-base md:text-lg font-semibold text-[#0B1528] mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /> Two-factor verification</h2>
          <p className="text-xs text-slate-500 mb-5">A second step is always required at sign-in. Available methods:</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {[
              { icon: Smartphone, label: "Authenticator app", on: user.totp_enabled, note: user.totp_enabled ? "Connected" : "Not set up — scan the QR at next sign-in" },
              { icon: Mail, label: "Email code", on: methods.email, note: methods.email ? `Sends to ${user.email}` : "Add an email that can receive mail" },
              { icon: MessageSquare, label: "SMS code", on: methods.sms, note: methods.sms ? `Sends to ${user.phone}` : "Needs Twilio credentials in the server settings" },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-slate-200 p-4" data-testid={`2fa-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between mb-2">
                  <m.icon className="h-4 w-4 text-slate-500" />
                  <Badge tone={m.on ? "green" : "slate"}>{m.on ? "Active" : "Off"}</Badge>
                </div>
                <p className="text-sm font-semibold text-[#0B1528]">{m.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.note}</p>
              </div>
            ))}
          </div>

          {!setup ? (
            <div className="flex flex-wrap items-end gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex-1 min-w-[220px]">
                <Label htmlFor="totp-pw">Confirm password to re-connect a new authenticator device</Label>
                <input id="totp-pw" type="password" value={totpPw} onChange={(e) => setTotpPw(e.target.value)} className={inputCls} data-testid="account-totp-password" />
              </div>
              <Button variant="ghost" onClick={resetTotp} loading={busy === "totp"} disabled={!totpPw} data-testid="account-totp-reset">Generate new QR</Button>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 grid sm:grid-cols-[auto_1fr] gap-5 items-center" data-testid="account-totp-setup">
              <img src={setup.qr} alt="Authenticator QR" className="h-40 w-40 rounded-lg border border-slate-200 bg-white" />
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0B1528] mb-1"><AlertTriangle className="h-4 w-4 text-amber-600" /> Your old authenticator codes no longer work.</p>
                <p className="text-xs text-slate-600 mb-3">Scan this QR in Google Authenticator / Authy, then enter the code to confirm. Manual key: <code className="break-all">{setup.secret}</code></p>
                <div className="flex gap-2">
                  <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="123456" maxLength={6} className={`${inputCls} w-40 text-center font-mono tracking-widest`} data-testid="account-totp-code" />
                  <Button onClick={confirmTotp} loading={busy === "confirm"} disabled={code.length !== 6} data-testid="account-totp-confirm"><CheckCircle2 className="h-4 w-4" /> Confirm</Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 flex flex-wrap items-center justify-between gap-4" data-testid="account-sessions">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-[#0B1528]">Sessions</h2>
            <p className="text-xs text-slate-500 mt-0.5">Last sign-in: {fmtDate(user.last_login_at)} · Sessions expire after 8 hours automatically.</p>
          </div>
          <Button variant="danger" onClick={() => run("all", async () => { await api.post("/auth/logout-all"); toast.success("All sessions signed out"); await logout(); })} loading={busy === "all"} data-testid="account-logout-all"><LogOut className="h-4 w-4" /> Sign out of all devices</Button>
        </Card>
      </div>
    </div>
  );
}
